import ModalDrawer from "components/source/shared/modal-drawer/modal-drawer";
import SelectedDates from "./calendar-selected-dates";
import RentalDurationToggle from "./rental-duration-toggle";
import ShipIcon from "images/svg/icon-ship.svg";
import RentalDateCalendar from "./calendar";
import { useState, type ChangeEvent, type FocusEvent } from "react";
import { displayModal } from "actions/shared-actions";
import { MODAL_NAMES } from "constants/modalNames";
import { addDays, endOfMonth, isAfter, startOfDay } from "date-fns";
import { parseISOWithoutTime } from "helpers/date-helpers";
import { debounce } from "helpers/utility-helpers";
import { useAppDispatch, useAppSelector } from "../../../../../../util/store-types";
import type { WorkingFilters } from "components/types/working-filters";
import type { WithRequiredFields } from "components/types/type-utils";

type CalendarDrawerModalProps = {
  onClickSave: (date: string | null, duration: NonNullable<WorkingFilters["duration"]>) => void;
  onZipSubmit: (zip: WithRequiredFields<WorkingFilters, "zip_code">) => void;
  zipCode?: string;
};

export default function CalendarDrawerModal({ onClickSave, onZipSubmit, zipCode }: CalendarDrawerModalProps) {
  const dispatch = useAppDispatch();
  const displayedModal = useAppSelector(state => state.displayedModal);
  const workingFiltersDuration = useAppSelector(state => state.workingFilters?.duration);
  const workingFiltersRentalDate = useAppSelector(state => state.workingFilters?.date);
  const dateOptions = useAppSelector(state => state.dateOptions || {});
  const deliveryCutoffs = useAppSelector(state => state.deliveryCutoffs);
  const [currentlySelectedDuration, setCurrentlySelectedDuration] = useState(workingFiltersDuration ?? "4");
  const [zipValue, setZipValue] = useState(zipCode);
  const [error, setError] = useState(false);
  const [selectedRentalDate, setSelectedRentalDate] = useState(workingFiltersRentalDate ?? null);
  const [lastRenderedCalendarDateState, setLastRenderedCalendarDateState] = useState<Date | null>(null);
  const [referenceDateValue, setReferenceDateValue] = useState(
    workingFiltersRentalDate ? parseISOWithoutTime(workingFiltersRentalDate) : new Date()
  );
  const regexForFiveDigits = /^\d{5}$/;

  const handleZipChange = debounce((value: string) => {
    onZipSubmit({ zip_code: value });
  }, 500);

  const closeCalendarDrawerModal = () => {
    if (displayedModal === MODAL_NAMES.CALENDAR_DRAWER) {
      return dispatch(displayModal(false));
    }
  };

  const resetDateSelection = () => {
    setSelectedRentalDate(null);
    setCurrentlySelectedDuration("4");
  };

  const handleSelectRentalDuration = () => {
    const newDuration = currentlySelectedDuration === "4" ? "8" : "4";
    setCurrentlySelectedDuration(newDuration);
    scrollCalendarToNextMonth(selectedRentalDate, lastRenderedCalendarDateState, newDuration);
  };

  const changeZip = (e: ChangeEvent<HTMLInputElement>) => {
    const value: string = e.target.value;
    setZipValue(e.target.value);

    if (!regexForFiveDigits.test(value)) {
      setError(true);
    } else if (regexForFiveDigits.test(value)) {
      handleZipChange(value);
      setError(false);
    }
  };

  const scrollCalendarToNextMonth = (
    date: string | null,
    lastRenderedCalendarDate: Date | null,
    rentalDuration: string
  ) => {
    if (!date || !lastRenderedCalendarDate) {
      return;
    }
    const rentalStartDate = parseISOWithoutTime(date);
    const startOfRentalEndDate = startOfDay(addDays(rentalStartDate, Number(rentalDuration) - 1));
    //lastRenderedCalendarDate gets updated when a date is clicked. If the reserve filter already has a date selected, default lastRenderedCalendarDate to the end of the month. It’s not a perfect solution, but it’s preferable to leaving it as null.
    const startOfLastRenderedCalendarDate =
      lastRenderedCalendarDate === null
        ? startOfDay(endOfMonth(rentalStartDate))
        : startOfDay(lastRenderedCalendarDate);

    if (isAfter(startOfRentalEndDate, startOfLastRenderedCalendarDate)) {
      setReferenceDateValue(startOfRentalEndDate);
    }
  };

  const handleClickDate = (date: string, lastRenderedCalendarDate: Date): void => {
    setLastRenderedCalendarDateState(lastRenderedCalendarDate);
    setSelectedRentalDate(date);
    scrollCalendarToNextMonth(date, lastRenderedCalendarDate, currentlySelectedDuration);
  };

  const handleClickSave = () => {
    const formattedRentalStartDate = selectedRentalDate ?? null;

    if (formattedRentalStartDate !== workingFiltersRentalDate || currentlySelectedDuration !== workingFiltersDuration) {
      onClickSave(formattedRentalStartDate, currentlySelectedDuration);
    }

    closeCalendarDrawerModal();
  };

  const onZipFocus = () => {
    setZipValue("");
  };

  const onZipBlur = (e: FocusEvent<HTMLInputElement>) => {
    const targetZipValue: string = e.target.value;
    if (regexForFiveDigits.test(targetZipValue)) {
      return;
    }
    setZipValue("Zip");
    setError(true);
  };

  const renderZipInput = () => {
    return (
      <div
        className={`flex items-center gap-1.5 rounded-md border ${
          error ? "border-mutedRed" : "border-lighterGray"
        } h-12 md:py-2 px-1 bg-white overflow-hidden`}>
        <ShipIcon className="md:h-5 mx-1" />
        <input
          type="text"
          value={zipValue}
          className={`!border-none !p-0 h-8 !text-md ${
            error ? "!text-mutedRed" : "text-black"
          } md:text-lg md:font-serif`}
          onChange={changeZip}
          onFocus={onZipFocus}
          onBlur={onZipBlur}
          maxLength={5}
          data-test-id="zip-code-input"
        />
      </div>
    );
  };

  const renderFooter = () => {
    const resetButtonDisabled = !selectedRentalDate;
    const saveButtonDisabled = zipValue?.length !== 5;
    const resetDisabledClass = resetButtonDisabled ? "text-lightGray border-lighterGray cursor-not-allowed" : "";
    const saveDisabledClass = saveButtonDisabled ? "cursor-not-allowed bg-darkerGray" : "bg-wine";
    const saveButtonCopy = saveButtonDisabled ? "Add Zip Code" : "Done";

    return (
      <div className="flex justify-between items-center mt-6 border-lighterGray bg-offWhite px-4 pb-2 gap-3 md:w-full md:min-w-96 md:sticky md:bottom-0 md:z-30 md:py-12 md:px-8 md:h-11 md:border-t">
        <button
          className={`flex flex-1 items-center justify-center bg-white border rounded-full px-4 py-2.5 md:h-11 md:w-52 ${resetDisabledClass}`}
          onClick={resetDateSelection}
          data-test-id="calendar-drawer-reset"
          disabled={resetButtonDisabled}>
          Reset
        </button>
        <button
          className={`flex flex-1 items-center justify-center rounded-full px-4 py-2.5 md:h-11 md:w-52 text-white ${saveDisabledClass}`}
          onClick={handleClickSave}
          data-test-id="calendar-drawer-done"
          disabled={saveButtonDisabled}>
          {saveButtonCopy}
        </button>
      </div>
    );
  };

  return (
    <ModalDrawer
      isOpen={displayedModal === MODAL_NAMES.CALENDAR_DRAWER}
      handleClose={closeCalendarDrawerModal}
      desktopWidth=""
      contentStyles={{ backgroundColor: "rgba(241, 240, 238, 1)" }}
      hideDragHandle={true}>
      <div className="flex flex-col gap-4 md:gap-6 md:mt-11 pt-11 md:pt-0">
        <div className="flex flex-col flex-start mr-10 ml-4 gap-1 md:gap-4">
          <p className="text-center text-lg font-medium md:font-light md:font-serif md:text-h3">
            For Special Occasions
          </p>
          <p className="text-sm md:text-md text-center">
            Reserve items for specific dates up to four months in advance. Includes one free backup size.
          </p>
        </div>
        <div className="flex flex-col mx-4 md:mx-8 gap-4">
          <hr className={"border-t border-lighterGray"}></hr>
          <div className="flex justify-between align-baseline">
            <p className="text-md md:text-lg">Rental Period</p>
            <RentalDurationToggle checked={currentlySelectedDuration === "8"} onChange={handleSelectRentalDuration} />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 justify-between">
              <div className="flex flex-col gap-1 min-w-52 w-full md:max-w-80">
                <p className="text-md text-left md:text-lg">Rental Dates</p>
                <SelectedDates
                  dateSelection={selectedRentalDate}
                  duration={currentlySelectedDuration}
                  setReferenceDateValue={setReferenceDateValue}
                />
              </div>
              <div className="flex flex-col items-start gap-1 w-full min-w-16 max-w-24">
                <p className="text-md md:text-lg">Ship to</p>
                {renderZipInput()}
              </div>
            </div>
            <p className="text-sm">Pro Tip: Select an arrival date two days before your event.</p>
          </div>
          <RentalDateCalendar
            dateSelection={selectedRentalDate}
            dateOptions={dateOptions}
            deliveryCutoffs={deliveryCutoffs}
            onClickDate={handleClickDate}
            duration={currentlySelectedDuration}
            referenceDateValue={referenceDateValue}
            setReferenceDateValue={setReferenceDateValue}
          />
        </div>
      </div>
      {renderFooter()}
    </ModalDrawer>
  );
}
